import { Feature, feature, point } from "@turf/helpers";
import { getGeom } from "@turf/turf";
import buffer from "@turf/buffer";
import { PickingInfo } from "@deck.gl/core";
import { SelectionFeature, SelectionGeometry, SelectionType } from "src/js/stores/map/store";
import { getDeckInstance } from "src/js/plugins/deck.gl/deckInstance";
import { designationGeometryByIdQuery } from "react-migration/domains/constraints/designation/hooks/useDesignationGeometryById";
import { createDesignationSelection } from "react-migration/domains/constraints/designation/util/createDesignationSelection";
import { getDesignationPointRadius } from "react-migration/domains/constraints/designation/style/accessors";
import { ClickSelectionResolver } from "react-migration/layouts/map/Multilayer/types";
import { createMultiLineString } from "react-migration/domains/constraints/designation/util/createMultiLineString";
import { createMultiPolygon } from "react-migration/domains/constraints/designation/util/createMultiPolygon";
import {
  DesignationFeature,
  DesignationLayer,
  DesignationLayerLight,
} from "react-migration/domains/constraints/components/ConstraintLayer/DesignationLayer";
import { LayerConstructor, getDeckLayerInstanceIds } from "react-migration/lib/map/getLayerInstances";
import { SELECTABLE_CATEGORIES } from "react-migration/domains/constraints/designation/style/StyleMap";
import { designationIsAssessable } from "./designationIsAssessable";

const POINT_BUFFER_RADIUS_METERS = 2;
const CONSTRAINT_LAYER_CLASSES = [DesignationLayer, DesignationLayerLight];

function isDesignationFeature(o: Feature): o is DesignationFeature {
  return !!o?.properties && "designation_id" in o.properties;
}

async function getPointSelectionGeometry(designation: DesignationFeature): Promise<SelectionGeometry | null> {
  const { data } = await designationGeometryByIdQuery(designation.properties.designation_id);
  const geometry = data.designation.designation_geometries[0]?.geometry;

  if ((geometry.type as any) === "MultiPoint") throw new Error("MultiPoint geometries are not supported");

  return geometry;
}

async function createPointSelection(
  designation: DesignationFeature,
  selectionType: SelectionType = SelectionType.DESIGNATION,
  zoomPointScale?: (zoom: number) => number
): Promise<SelectionFeature> {
  const isAssessable = designationIsAssessable(designation);
  const geometry = await getPointSelectionGeometry(designation);

  // add a buffer so that features at this same location are returned in considerations
  const areaAroundPoint =
    geometry?.type === "Point" && getGeom(buffer(geometry, POINT_BUFFER_RADIUS_METERS, { units: "meters" }));

  return {
    type: selectionType,
    id: designation.properties.designation_id,
    feature: areaAroundPoint ? feature(areaAroundPoint, designation.properties) : undefined,
    savable: false,
    __zoomPointScale: zoomPointScale,
    __renderGeometry: geometry
      ? point(geometry.coordinates as [number, number], {
          radius: getDesignationPointRadius(designation),
        })
      : undefined,
    hideAssessment: !isAssessable,
  };
}

const processDesignationFeature = async (
  object: DesignationFeature,
  zoomPointScale?: (zoom: number) => number,
  selectionType?: SelectionType
): Promise<SelectionFeature | null> => {
  switch (object.geometry.type) {
    case "Point": {
      return createPointSelection(object, selectionType, zoomPointScale);
    }

    case "Polygon":
    case "MultiPolygon": {
      return createDesignationSelection(object, createMultiPolygon, selectionType);
    }

    case "LineString":
    case "MultiLineString": {
      return createDesignationSelection(object, createMultiLineString, selectionType);
    }
  }
  return null;
};

export const resolveConstraintClick: ClickSelectionResolver = async (info: PickingInfo<any>) => {
  const { object } = info;
  if (isDesignationFeature(object)) {
    const zoomPointScale = info.layer?.state?.zoomPointScale as (zoom: number) => number;
    return processDesignationFeature(object, zoomPointScale);
  }

  return null;
};

type CreateConstraintLikeClickResolverParams = {
  layerClasses?: LayerConstructor[];
  selectionType?: SelectionType;
  selectableCategories?: string[];
};

export function createConstraintLikeClickResolver({
  layerClasses = CONSTRAINT_LAYER_CLASSES,
  selectableCategories = SELECTABLE_CATEGORIES,
  selectionType = SelectionType.DESIGNATION,
}: CreateConstraintLikeClickResolverParams = {}): ClickSelectionResolver {
  return async (info: PickingInfo<unknown>) => {
    const { x, y } = info;
    const deck = getDeckInstance();
    const layerIds = getDeckLayerInstanceIds(layerClasses);
    let picked = deck?.pickMultipleObjects({ x, y, layerIds }) || [];

    // TODO this is a hack to allow object which are directly picked being
    // selected. The real issue is that if the source DesignationLayer was
    // rendered as a child of another CompositeLayer the above
    // getDeckLayerInstanceIds/pickMultipleObjects logic won't return it
    if (layerClasses.some((C) => info.sourceLayer instanceof C)) {
      picked = [info, ...picked];
    }

    for (const { object } of picked) {
      if (isDesignationFeature(object) && selectableCategories.includes(object.properties.sub_category_id || "")) {
        const zoomPointScale = info.layer?.state?.zoomPointScale as (zoom: number) => number;
        const feature = await processDesignationFeature(object, zoomPointScale, selectionType);
        if (feature) return feature;
      }
    }

    return null;
  };
}

export const constraintClickResolver = createConstraintLikeClickResolver();
